import { useTranslation } from 'next-i18next';
import React, { FC, useEffect, useRef, useState } from 'react';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useElementSize, useViewportSize } from '@hotelplan/libs.hooks-dom';
import {
  Container,
  SlidersNavigationContainer,
} from 'components/domain/container';
import { AdaptiveCardsSlider } from 'components/domain/customizable-slider/CardsSection';
import { getFdrThGeoSiblingsObject } from 'components/domain/fdr-geo/fdr-geo.utils';
import {
  GridRecommendationItem,
  GridRecommendationMobileButtonItems,
  GridRecommendationTitle,
} from 'components/domain/recommendations/CommonGridRecommendationSection.styles';
import { PaginationArrowClickSource } from 'components/domain/tracking/paginationTracking';
import {
  fdrGeoPageSiblingsDesktopVars,
  fdrGeoPageSiblingsMobileVars,
  SIBLINGS_DEFAULT_RESULT_PER_PAGE,
} from 'components/page/geo/geo.vars';
import { useFdrGeoSiblingsQuery } from 'schemas/query/geo/fdr-geo-siblings.generated';
import { GridRecommendationItemImage } from './fdr-geo-recommender-item';
import {
  ChevronIcon,
  MobileListItems,
  SimilarDestinationsSection,
} from './fdr-similar-destinations-recommender.styles';
import FdrSimilarDestinationsSkeleton from './fdr-similar-destinations.skeleton';
const MOBILE_SIZE = 480;
const DEFAULT_IMAGE_HEIGHT = 274;

const headerStyle = { color: 'black', marginBottom: '32px' };

interface ISimilarDestinationsRecommendationProps {
  id: string;
}

const siblingsPageDefault = (width: number, mobile: boolean) => {
  return width >= MOBILE_SIZE || !mobile
    ? fdrGeoPageSiblingsDesktopVars
    : fdrGeoPageSiblingsMobileVars;
};

const FdrSimilarDestinationsRecommendation: FC<ISimilarDestinationsRecommendationProps> =
  ({ id }) => {
    const { t } = useTranslation('geo');
    const { width } = useViewportSize();
    const { mobile } = useDeviceType();
    const [page, setPage] = useState(0);
    const imageRef = useRef<HTMLDivElement>(null);
    const uriRef = useRef<string>(null);
    const isSliderPagination = !mobile;
    const { h } = useElementSize(imageRef);

    const isCards = width >= MOBILE_SIZE || !mobile;

    const { data, fetchMore, loading } = useFdrGeoSiblingsQuery({
      variables: {
        input: { publicId: id },
        ...siblingsPageDefault(width, mobile),
      },
    });

    const fdrGeo = getFdrThGeoSiblingsObject(data);

    const title = fdrGeo?.parent?.name || '';
    const pageNumber = fdrGeo?.siblings?.pagination?.page || 0;
    const resultsTotal = fdrGeo?.siblings?.pagination?.total || 0;

    function onNextPage() {
      setPage(current => current + 1);
      if (pageNumber >= page + 1) return;
      fetchMore({
        variables: {
          input: { publicId: id },
          siblingsInput: {
            pagination: {
              page: page + 1,
              perPage: SIBLINGS_DEFAULT_RESULT_PER_PAGE,
            },
          },
        },
      });
    }

    function onPrevPage() {
      setPage(current => current - 1);
    }

    function onChangePage(nextPage: number): void {
      setPage(nextPage);
      fetchMore({
        variables: {
          input: { publicId: id },
          siblingsInput: {
            pagination: {
              page: nextPage,
              perPage: SIBLINGS_DEFAULT_RESULT_PER_PAGE,
            },
          },
        },
      });
    }
    useEffect(() => {
      if (!uriRef.current) {
        uriRef.current = id;
      }
      if (uriRef?.current && uriRef.current !== id) {
        setPage(0);
      }
    }, [id]);

    const ContainerVariant = isSliderPagination
      ? SlidersNavigationContainer
      : Container;

    if (!fdrGeo?.siblings?.page?.length) return null;

    return (
      <ContainerVariant>
        <SimilarDestinationsSection
          title={t(
            fdrGeo.siblings.page.length === 1
              ? 'siblings.title'
              : 'siblings.several.title',
            { destination: title }
          )}
          totalCount={isSliderPagination && resultsTotal}
          sectionTitle={isSliderPagination && t('siblings.section.title')}
          page={page}
          totalPages={Math.ceil(
            resultsTotal / SIBLINGS_DEFAULT_RESULT_PER_PAGE
          )}
          onNextPage={onNextPage}
          onPrevPage={onPrevPage}
          headerStyle={headerStyle}
          loading={loading}
          isCards={isCards}
          isSliderPagination={isSliderPagination}
          imageHeight={h || DEFAULT_IMAGE_HEIGHT}
          trackingSource={PaginationArrowClickSource.SIMILAR_DESTINATION}
        >
          {isCards ? (
            <AdaptiveCardsSlider
              page={page}
              onChangePage={onChangePage}
              perPage={SIBLINGS_DEFAULT_RESULT_PER_PAGE}
              total={resultsTotal || fdrGeo.siblings.page.length}
              placeholder={FdrSimilarDestinationsSkeleton}
              hideDelimiter
            >
              {fdrGeo.siblings.page.map((item, index) =>
                item ? (
                  <FdrRoute
                    key={`siblings-item-${index}`}
                    link={item.webMeta.descriptionWebMeta.link}
                  >
                    <div className="siblings-item-wrapper">
                      <GridRecommendationItem
                        className="siblings-item"
                        ref={index === 0 ? imageRef : undefined}
                      >
                        <GridRecommendationItemImage
                          image={item.hero}
                          lazy={index + 1 > SIBLINGS_DEFAULT_RESULT_PER_PAGE}
                          height={DEFAULT_IMAGE_HEIGHT}
                          width={393}
                        />
                      </GridRecommendationItem>
                      <GridRecommendationTitle className="siblings-title">
                        <span>{item.name}</span>
                        <ChevronIcon />
                      </GridRecommendationTitle>
                    </div>
                  </FdrRoute>
                ) : null
              )}
            </AdaptiveCardsSlider>
          ) : (
            <MobileListItems>
              {fdrGeo.siblings.page.map((item, index) =>
                item ? (
                  <FdrRoute
                    key={`siblings-item-${index}`}
                    link={item.webMeta.descriptionWebMeta.link}
                  >
                    <div className="siblings-item-wrapper">
                      <GridRecommendationMobileButtonItems>
                        <GridRecommendationTitle className="siblings-title">
                          <span>{item.name}</span>
                          <ChevronIcon />
                        </GridRecommendationTitle>
                      </GridRecommendationMobileButtonItems>
                    </div>
                  </FdrRoute>
                ) : null
              )}
            </MobileListItems>
          )}
        </SimilarDestinationsSection>
      </ContainerVariant>
    );
  };

export default FdrSimilarDestinationsRecommendation;
